import React, { useEffect, useState, useRef } from 'react';
import {
  MapContainer, TileLayer, Marker, Popup,
  Circle, Polyline, useMap
} from 'react-leaflet';
import L from 'leaflet';
import { io } from 'socket.io-client';
import axios from 'axios';
import { format } from 'date-fns';
import { fmtTimeSec, fmtTime } from '../utils/time';
import { MAP_TILES, DEFAULT_TILE } from '../utils/mapTiles';

// ── Leaflet icon fix ───────────────────────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// ── Icons ──────────────────────────────────────────────────────
const makeGuardIcon = (name, isStale) => L.divIcon({
  html: `<div style="
    background:${isStale ? '#9ca3af' : '#4361ee'};
    border:3px solid white;border-radius:50%;
    width:32px;height:32px;
    display:flex;align-items:center;justify-content:center;
    font-size:15px;box-shadow:0 2px 10px rgba(0,0,0,.35);
    position:relative;
  ">👮
    ${!isStale ? `<div style="
      position:absolute;top:-2px;right:-2px;
      width:10px;height:10px;border-radius:50%;
      background:#22c55e;border:2px solid white;
    "></div>` : ''}
  </div>
  <div style="
    background:rgba(0,0,0,.7);color:white;
    font-size:9px;font-weight:600;
    border-radius:4px;padding:2px 5px;
    margin-top:2px;white-space:nowrap;text-align:center;
    max-width:80px;overflow:hidden;text-overflow:ellipsis;
  ">${name}</div>`,
  className: '', iconSize: [32, 48], iconAnchor: [16, 16],
});

const cpDoneIcon   = L.divIcon({ html: `<div style="background:#22c55e;border:2px solid white;border-radius:50%;width:16px;height:16px;box-shadow:0 1px 4px rgba(0,0,0,.3)"></div>`, className:'', iconSize:[16,16], iconAnchor:[8,8] });
const cpPendIcon   = L.divIcon({ html: `<div style="background:#9ca3af;border:2px solid white;border-radius:50%;width:16px;height:16px;box-shadow:0 1px 4px rgba(0,0,0,.3)"></div>`, className:'', iconSize:[16,16], iconAnchor:[8,8] });

// ── Auto-center map to guard ────────────────────────────────────
function FlyToGuard({ pos, follow }) {
  const map = useMap();
  const prev = useRef(null);
  useEffect(() => {
    if (!follow || !pos || (prev.current?.[0] === pos[0] && prev.current?.[1] === pos[1])) return;
    map.panTo(pos, { animate: true, duration: 0.6 });
    prev.current = pos;
  }, [pos, follow]);
  return null;
}

// ── Tile layers ────────────────────────────────────────────────
const TILES = MAP_TILES;

const isValidCoord = (lat, lng) => {
  if (lat == null || lng == null) return false;
  const nLat = parseFloat(lat);
  const nLng = parseFloat(lng);
  if (isNaN(nLat) || isNaN(nLng)) return false;
  if (nLat === 0 && nLng === 0) return false;
  return true;
};


export default function LiveMap() {
  const [guards, setGuards]           = useState({});   // guardId → latest data
  const [trails, setTrails]           = useState({});   // guardId → [[lat,lng],...]
  const [activePatrols, setActivePatrols] = useState([]);
  const [checkpointsByPatrol, setCPsByPatrol] = useState({}); // patrolId → [cps]
  const [selectedGuard, setSelectedGuard]     = useState(null);
  const [tile, setTile]               = useState(DEFAULT_TILE);
  const [followGuard, setFollowGuard] = useState(false);
  const [followId, setFollowId]       = useState(null);
  const [lastUpdate, setLastUpdate]   = useState(null);
  const [socketStatus, setSocketStatus] = useState('connecting'); // connecting | connected | disconnected
  const socketRef = useRef(null);
  const refreshRef = useRef(null);

  // Stale = no update in 60s
  const isStale = (ts) => ts && (Date.now() - new Date(ts).getTime()) > 60000;

  // Load active patrols + their checkpoints + last known positions
  const loadPatrols = async () => {
    const res = await axios.get('/api/patrols');
    const active = res.data.filter(p => p.status === 'active');
    setActivePatrols(active);

    // Load checkpoints for each active patrol
    const cpMap = {};
    await Promise.all(active.map(async p => {
      try {
        const r = await axios.get(`/api/checkpoints/route/${p.route_id}`);
        cpMap[p.id] = r.data;
      } catch { cpMap[p.id] = []; }
    }));
    setCPsByPatrol(cpMap);

    // Load last known positions for all active patrols
    try {
      const posRes = await axios.get('/api/patrols/active-positions');
      posRes.data.forEach(pos => {
        if (!pos.latitude || !pos.longitude) return;
        setGuards(prev => ({
          ...prev,
          [pos.guardId]: {
            guardId:   pos.guardId,
            guardName: pos.guardName,
            patrolId:  pos.patrolId,
            latitude:  pos.latitude,
            longitude: pos.longitude,
            timestamp: pos.timestamp || new Date(),
          },
        }));
        setTrails(prev => ({
          ...prev,
          [pos.guardId]: prev[pos.guardId] || [[pos.latitude, pos.longitude]],
        }));
      });
    } catch (_) { /* active-positions may fail on older DB — silent */ }
  };

  useEffect(() => {
    loadPatrols();
    refreshRef.current = setInterval(loadPatrols, 15000); // refresh every 15s

    // Socket — use polling first so it works on Render free tier
    const url = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.hostname.includes('pages.dev') ? 'https://guard-patrol-73go.onrender.com' : 'http://localhost:5003');
    const socket = io(url, {
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      timeout: 20000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('LiveMap socket connected');
      setSocketStatus('connected');
    });

    socket.on('disconnect', () => setSocketStatus('disconnected'));
    socket.on('connect_error', () => setSocketStatus('disconnected'));

    socket.on('guard:location:update', (data) => {
      setLastUpdate(new Date());
      // Handle both field name styles
      const guardId   = data.guardId   || data.guard_id;
      const guardName = data.guardName || data.guard_name || 'Guard';
      const patrolId  = data.patrolId  || data.patrol_id;
      const latitude  = data.latitude  || data.lat;
      const longitude = data.longitude || data.lng;
      const timestamp = data.timestamp || new Date();

      if (!guardId || !latitude || !longitude) return;

      setGuards(prev => ({
        ...prev,
        [guardId]: { guardId, guardName, patrolId, latitude, longitude, timestamp },
      }));

      setTrails(prev => ({
        ...prev,
        [guardId]: [...(prev[guardId] || []).slice(-200), [latitude, longitude]],
      }));
    });

    // Guard stopped patrol
    socket.on('patrol:stopped', () => { loadPatrols(); });

    return () => {
      socket.disconnect();
      clearInterval(refreshRef.current);
    };
  }, []);

  const guardList   = Object.values(guards);
  const onlineCount = guardList.filter(g => !isStale(g.timestamp)).length;

  // Center map — first guard or Gujarat default
  const mapCenter = guardList.length > 0
    ? [guardList[0].latitude, guardList[0].longitude]
    : [22.2587, 71.1924]; // Gujarat, India

  const followPos = followGuard && followId && guards[followId]
    ? [guards[followId].latitude, guards[followId].longitude]
    : null;

  const selectedPatrol = selectedGuard
    ? activePatrols.find(p => p.guard_id === selectedGuard || p.id === guards[selectedGuard]?.patrolId)
    : null;

  const selectedCPs = selectedPatrol ? (checkpointsByPatrol[selectedPatrol.id] || []) : [];

  return (
    <div>
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="page-header" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="page-title">🗺️ Live Guard Map</div>
          {lastUpdate && (
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
              Last update: {format(lastUpdate, 'HH:mm:ss')}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Online / patrols badges */}
          <span className="badge badge-success">🟢 {onlineCount} Online</span>
          <span className="badge badge-warning">🚶 {activePatrols.length} Patrols</span>
          <span className="badge badge-info">📍 {Object.keys(guards).length} Tracked</span>
          <span style={{
            fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600,
            background: socketStatus === 'connected' ? '#dcfce7' : socketStatus === 'connecting' ? '#fef3c7' : '#fee2e2',
            color: socketStatus === 'connected' ? '#166534' : socketStatus === 'connecting' ? '#92400e' : '#991b1b',
          }}>
            {socketStatus === 'connected' ? '🔌 Socket Live' : socketStatus === 'connecting' ? '⏳ Connecting...' : '❌ Socket Off'}
          </span>

          {/* Tile selector */}
          <div style={{ display: 'flex', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
            {Object.values(TILES).map(t => (
              <button key={t.id} onClick={() => setTile(t.id)}
                style={{
                  padding: '5px 12px', border: 'none', cursor: 'pointer', fontSize: 12,
                  background: tile === t.id ? '#4361ee' : 'white',
                  color: tile === t.id ? 'white' : '#374151',
                  fontWeight: tile === t.id ? 600 : 400,
                }}>
                {t.name}
              </button>
            ))}
          </div>

          {/* Follow toggle */}
          <button
            onClick={() => setFollowGuard(f => !f)}
            style={{
              padding: '5px 14px', borderRadius: 8, border: '1px solid',
              borderColor: followGuard ? '#4361ee' : '#e5e7eb',
              background: followGuard ? '#eef2ff' : 'white',
              color: followGuard ? '#4361ee' : '#374151',
              fontSize: 12, cursor: 'pointer', fontWeight: followGuard ? 600 : 400,
            }}>
            {followGuard ? '📌 Following' : '📌 Follow Guard'}
          </button>

          <button onClick={loadPatrols} className="btn btn-secondary btn-sm">🔄 Refresh</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedGuard ? '1fr 300px' : '1fr', gap: 16 }}>

        {/* ── MAP ─────────────────────────────────────────── */}
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <MapContainer center={mapCenter} zoom={guardList.length > 0 ? 16 : 7}
            maxZoom={22}
            style={{ height: 580, width: '100%' }}>
            <TileLayer
              key={tile}
              url={TILES[tile]?.url || TILES.googleHybrid.url}
              attribution={TILES[tile]?.attr || '© Google Maps'}
              maxZoom={22}
              maxNativeZoom={TILES[tile]?.maxNativeZoom || 21}
            />

            {/* Follow guard */}
            {followPos && <FlyToGuard pos={followPos} follow={followGuard} />}

            {/* Trails */}
            {Object.entries(trails).map(([id, pts]) =>
              pts.length > 1 && (
                <Polyline key={id} positions={pts}
                  color={selectedGuard === id ? '#4361ee' : '#94a3b8'}
                  weight={selectedGuard === id ? 3 : 2}
                  opacity={selectedGuard === id ? 0.8 : 0.5}
                  dashArray="6 4"
                />
              )
            )}

            {/* Guard markers */}
            {guardList.map(g => {
              const stale = isStale(g.timestamp);
              return (
                <Marker
                  key={g.guardId}
                  position={[g.latitude, g.longitude]}
                  icon={makeGuardIcon(g.guardName || 'Guard', stale)}
                  eventHandlers={{
                    click: () => {
                      setSelectedGuard(g.guardId === selectedGuard ? null : g.guardId);
                      setFollowId(g.guardId);
                    }
                  }}
                >
                  <Popup>
                    <div style={{ minWidth: 160 }}>
                      <strong>👮 {g.guardName}</strong><br />
                      <span style={{ color: stale ? '#ef233c' : '#22c55e', fontWeight: 600 }}>
                        {stale ? '⚠️ Offline' : '🟢 Active'}
                      </span><br />
                      Lat: {parseFloat(g.latitude).toFixed(6)}<br />
                      Lng: {parseFloat(g.longitude).toFixed(6)}<br />
                      Last: {g.timestamp ? fmtTimeSec(g.timestamp) : '—'}
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Checkpoints for selected guard's patrol */}
            {selectedCPs.filter(cp => isValidCoord(cp.latitude, cp.longitude)).map(cp => {
              const done = selectedPatrol && selectedPatrol.completed_checkpoints > 0;
              return (
                <React.Fragment key={cp.id}>
                  <Circle
                    center={[parseFloat(cp.latitude), parseFloat(cp.longitude)]}
                    radius={cp.radius_meters || 30}
                    color="#4361ee" fillOpacity={0.1} weight={1.5}
                  />
                  <Marker position={[parseFloat(cp.latitude), parseFloat(cp.longitude)]} icon={cpPendIcon}>
                    <Popup>CP{cp.sequence_order}: {cp.name}</Popup>
                  </Marker>
                </React.Fragment>
              );
            })}
          </MapContainer>

          {/* No guards notice */}
          {guardList.length === 0 && (
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
              background: 'rgba(255,255,255,.9)', padding: '16px 24px', borderRadius: 12,
              textAlign: 'center', pointerEvents: 'none', boxShadow: '0 4px 20px rgba(0,0,0,.15)',
            }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>👮</div>
              <div style={{ fontWeight: 600 }}>No guards online</div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                Start the Guard Simulator to see live tracking
              </div>
            </div>
          )}
        </div>

        {/* ── SELECTED GUARD DETAIL PANEL ─────────────────── */}
        {selectedGuard && guards[selectedGuard] && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Guard info */}
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>👮 Guard Detail</div>
                <button onClick={() => setSelectedGuard(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18 }}>✕</button>
              </div>
              {(() => {
                const g = guards[selectedGuard];
                const stale = isStale(g.timestamp);
                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <div style={{
                        width: 44, height: 44, borderRadius: '50%', background: '#eef2ff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
                      }}>👮</div>
                      <div>
                        <div style={{ fontWeight: 700 }}>{g.guardName}</div>
                        <div style={{ fontSize: 12, color: stale ? '#ef233c' : '#22c55e', fontWeight: 600 }}>
                          {stale ? '⚠️ Offline / Stale' : '🟢 Active'}
                        </div>
                      </div>
                    </div>
                    {[
                      { l: 'Latitude',    v: parseFloat(g.latitude).toFixed(6) },
                      { l: 'Longitude',   v: parseFloat(g.longitude).toFixed(6) },
                      { l: 'Last Ping',   v: g.timestamp ? fmtTimeSec(g.timestamp) : '—' },
                      { l: 'Trail Points', v: (trails[selectedGuard]||[]).length },
                    ].map(r => (
                      <div key={r.l} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                        <span style={{ color: '#9ca3af' }}>{r.l}</span>
                        <span style={{ fontWeight: 500 }}>{r.v}</span>
                      </div>
                    ))}
                    <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => { setFollowGuard(true); setFollowId(selectedGuard); }}
                        className="btn btn-primary btn-sm" style={{ flex: 1 }}>
                        📌 Follow
                      </button>
                      <button
                        onClick={() => setTrails(prev => ({ ...prev, [selectedGuard]: [] }))}
                        className="btn btn-secondary btn-sm">
                        🗑 Trail
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Active patrol info */}
            {selectedPatrol && (
              <div className="card">
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>🚶 Active Patrol</div>
                {[
                  { l: 'Route',      v: selectedPatrol.route_name },
                  { l: 'Site',       v: selectedPatrol.site_name },
                  { l: 'Started',    v: fmtTime(selectedPatrol.started_at) },
                  { l: 'Checkpoints', v: `${selectedPatrol.completed_checkpoints}/${selectedPatrol.total_checkpoints}` },
                  { l: 'Compliance', v: `${selectedPatrol.compliance_percent}%` },
                ].map(r => (
                  <div key={r.l} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                    <span style={{ color: '#9ca3af' }}>{r.l}</span>
                    <span style={{ fontWeight: 500 }}>{r.v}</span>
                  </div>
                ))}

                {/* Compliance bar */}
                <div style={{ marginTop: 10 }}>
                  <div style={{ background: '#f3f4f6', borderRadius: 4, height: 8 }}>
                    <div style={{
                      width: `${selectedPatrol.compliance_percent}%`, height: '100%', borderRadius: 4,
                      background: selectedPatrol.compliance_percent >= 80 ? '#22c55e' : '#f59e0b',
                      transition: 'width .5s',
                    }} />
                  </div>
                </div>

                {/* Checkpoints list */}
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 6 }}>Checkpoints</div>
                  {selectedCPs.map(cp => (
                    <div key={cp.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#9ca3af', flexShrink: 0 }} />
                      <span>CP{cp.sequence_order}: {cp.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Active Patrols Table ─────────────────────────── */}
      <div className="card" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>🚶 Active Patrols</div>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>Auto-refreshes every 15s</span>
        </div>
        {activePatrols.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: 13, padding: '16px 0', textAlign: 'center' }}>
            No active patrols — start the Guard Simulator to see one here
          </div>
        ) : (
          <div className="table-scroll">
            <table className="table" style={{ fontSize: 13 }}>
              <thead>
                <tr><th>Guard</th><th>Route</th><th>Site</th><th>Started</th><th>Checkpoints</th><th>Compliance</th><th>GPS Status</th></tr>
              </thead>
              <tbody>
                {activePatrols.map(p => {
                  const gData  = Object.values(guards).find(g => g.patrolId === p.id || g.guardId === p.guard_id);
                  const online = gData && !isStale(gData?.timestamp);
                  return (
                    <tr key={p.id} style={{ cursor: 'pointer' }}
                      onClick={() => { setSelectedGuard(gData?.guardId || null); setFollowId(gData?.guardId); }}>
                      <td><b>👮 {p.guard_name}</b></td>
                      <td>{p.route_name}</td>
                      <td style={{ color: '#9ca3af' }}>{p.site_name}</td>
                      <td>{fmtTime(p.started_at)}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ flex: 1, background: '#f3f4f6', borderRadius: 3, height: 6 }}>
                            <div style={{
                              width: `${p.total_checkpoints > 0 ? (p.completed_checkpoints / p.total_checkpoints) * 100 : 0}%`,
                              height: '100%', borderRadius: 3, background: '#4361ee',
                            }} />
                          </div>
                          <span style={{ fontSize: 11, color: '#666' }}>{p.completed_checkpoints}/{p.total_checkpoints}</span>
                        </div>
                      </td>
                      <td style={{ fontWeight: 700, color: p.compliance_percent >= 80 ? '#22c55e' : '#f59e0b' }}>
                        {p.compliance_percent}%
                      </td>
                      <td>
                        <span style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 600,
                          background: online ? '#dcfce7' : gData ? '#fee2e2' : '#f3f4f6',
                          color: online ? '#166534' : gData ? '#991b1b' : '#6b7280',
                        }}>
                          {online ? '🟢 Live' : gData ? '🔴 Stale' : '⚫ No GPS'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
